/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { Box, CircularProgress } from '@mui/material';

import { BasicRoutes } from '../../constants/basicRoutes';
import { Namespaces } from '../../i18n';
import { removeNotification } from '../../store/notifications/actions';
import { INotification } from '../../store/notifications/types';
import { AppServerEvents } from '../../types/common/AppEvents';
import { DatasetImportOperationStatus } from '../../types/dataset/DatasetImportOperationStatus';
import { NotificationItemFrameOptions } from '../../types/notification/NotificationItemFrameOptions';
import { getDSImportOperationStatusColor } from '../../utils/getDSImportOperationStatusColor';
import { prepareRoutePath } from '../../utils/prepareRoutePath';
import { fetchCluster } from '../../utils/queryFns';
import { useNotificationRelativeDate } from '../../utils/useNotificationRelativeDate';
import { AlignCenter } from '../AlignCenter/AlignCenter';
import { NotificationItemFrame } from '../NotificationItemFrame/NotificationItemFrame';

type DatasetImportEvents =
  | AppServerEvents.ImportStarted
  | AppServerEvents.ImportSuccessful
  | AppServerEvents.ImportAborted
  | AppServerEvents.ImportFailedInsufficientSpace
  | AppServerEvents.ImportFailedSchemaExists
  | AppServerEvents.ImportFailedDatabaseNotFound
  | AppServerEvents.ImportFailedInvalidCredentials
  | AppServerEvents.ImportFailed;

type DatasetImportNotificationProps = NotificationItemFrameOptions & {
  notification: INotification<DatasetImportEvents>;
};

export function DatasetImportNotification(props: DatasetImportNotificationProps) {
  const { notification, setNotificationAsRead, asSnackbar, onRemoveBtnClick, onRootClick } = props;
  const { event, createdAt, isRead } = notification;
  const { clusterId, organizationId, projectId, datasetName } = event;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { t } = useTranslation(Namespaces.Entities);

  const clusterQuery = useQuery(
    [
      fetchCluster.key,
      {
        organizationId,
        clusterId,
        projectId,
      },
    ],
    fetchCluster.fn,
    { refetchOnWindowFocus: false, retry: 1 },
  );

  const clusterData = useMemo(() => clusterQuery.data?.payload, [clusterQuery.data]);

  const [datetime] = useNotificationRelativeDate(createdAt);

  const { status, statusColor } = useMemo<{
    status:
      | DatasetImportOperationStatus.Importing
      | DatasetImportOperationStatus.Successful
      | DatasetImportOperationStatus.Failure
      | DatasetImportOperationStatus.Aborted;
    statusColor?: string;
  }>(() => {
    switch (notification.type) {
      case AppServerEvents.ImportStarted:
        return { status: DatasetImportOperationStatus.Importing };
      case AppServerEvents.ImportSuccessful:
        return { status: DatasetImportOperationStatus.Successful };
      case AppServerEvents.ImportAborted:
        return {
          status: DatasetImportOperationStatus.Aborted,
          statusColor: getDSImportOperationStatusColor(DatasetImportOperationStatus.Importing),
        };
      default:
        return { status: DatasetImportOperationStatus.Failure };
    }
  }, [notification]);

  const totalStatusColor = useMemo<string>(
    () => statusColor || getDSImportOperationStatusColor(status),
    [status, statusColor],
  );

  const onClick = () => {
    onRootClick && onRootClick({ id: notification.id });
    void navigate(
      prepareRoutePath(BasicRoutes.CLUSTER_DATASETS_PAGE, {
        ':organization_id': organizationId,
        ':project_id': projectId,
        ':cluster_id': clusterId,
      }),
    );
  };

  useEffect(() => {
    if (clusterQuery.status === 'error') {
      dispatch(removeNotification({ id: notification.id }));
    }
  }, [clusterQuery.status, dispatch, notification.id]);

  return clusterData ? (
    <NotificationItemFrame
      setNotificationAsRead={setNotificationAsRead}
      datetime={datetime}
      isRead={asSnackbar ? true : isRead}
      id={notification.id}
      onRemoveBtnClick={onRemoveBtnClick}
      asSnackbar={asSnackbar}
      onRootClick={onClick}
      primaryText={
        <>
          {datasetName}
          &nbsp;
          <span style={{ color: totalStatusColor }}>{t(`dataset.status.values.${status.toLowerCase()}`)}</span>
        </>
      }
      secondaryText={t('dataset.status.secondary_text', {
        cluster: clusterData?.name,
      })}
    />
  ) : (
    <AlignCenter>
      <Box sx={{ padding: '5px' }}>
        <CircularProgress color="primary" size={20} />
      </Box>
    </AlignCenter>
  );
}
